import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, ChevronRight, Download, File, FilePlus, Folder, FolderOpen, FolderPlus, Home, Link2, Pencil, RefreshCw, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import type { SiteTools } from "@hostdock/sdk";
import type { FileEntry, WebsiteDetail } from "@hostdock/types";
import { Button } from "../button";
import { formatBytes } from "../chart";
import { ConfirmDialog, Dialog } from "../dialog";
import { EmptyState, ErrorState, FormError, Notice } from "../feedback";
import { Field, Input, Textarea } from "../form";
import { formatDateTime } from "../format";
import { ActionMenu } from "../menu";
import { toast } from "../toast";
import { TabCard, tabPrimaryClass, tabSecondaryClass } from "./tab-card";

const join = (dir: string, name: string) => (dir === "/" ? `/${name}` : `${dir}/${name}`);
const parent = (p: string) => (p === "/" ? "/" : p.slice(0, p.lastIndexOf("/")) || "/");

interface UploadProgress {
  name: string;
  sent: number;
  size: number;
}

/**
 * File Manager. Every operation runs on the node as the website's own user and
 * cannot leave the website's home; large transfers are chunked (1 MB).
 */
export function WebsiteFiles({ w, tools, canWrite }: { w: WebsiteDetail; tools: SiteTools; canWrite: boolean }) {
  const qc = useQueryClient();
  const [dir, setDir] = useState("/public");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState<"file" | "folder" | null>(null);
  const [renaming, setRenaming] = useState<FileEntry | null>(null);
  const [chmodding, setChmodding] = useState<FileEntry | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string[] | null>(null);
  const [upload, setUpload] = useState<UploadProgress | null>(null);
  const abort = useRef<AbortController | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const deployed = w.status === "ACTIVE" || w.status === "SUSPENDED" || w.status === "FAILED";

  const q = useQuery({ queryKey: ["files", w.id, dir], queryFn: () => tools.files.list(w.id, dir), enabled: deployed, retry: false });
  const refresh = () => {
    setSelected(new Set());
    void qc.invalidateQueries({ queryKey: ["files", w.id] });
  };
  const remove = useMutation({ meta: { inlineError: true },
    mutationFn: (paths: string[]) => tools.files.remove(w.id, paths),
    onSuccess: (r) => { toast.success(`Deleted ${r.deleted} item${r.deleted === 1 ? "" : "s"}`); setDeleting(null); refresh(); },
  });

  async function uploadFiles(files: FileList) {
    const ctrl = new AbortController();
    abort.current = ctrl;
    for (const file of Array.from(files)) {
      let uploadId: string | null = null;
      try {
        const init = await tools.files.startUpload(w.id, dir, file.name, file.size);
        uploadId = init.uploadId;
        setUpload({ name: file.name, sent: 0, size: file.size });
        let offset = 0;
        do {
          const chunk = file.slice(offset, offset + init.chunkSize);
          const r = await tools.files.uploadChunk(w.id, init.uploadId, offset, chunk, ctrl.signal);
          offset = r.received;
          setUpload({ name: file.name, sent: offset, size: file.size });
        } while (offset < file.size);
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        if (uploadId) await tools.files.abortUpload(w.id, uploadId).catch(() => undefined);
        if ((err as Error).name === "AbortError") toast.error("Upload cancelled");
        else toast.error(`${file.name}: ${(err as Error).message}`);
        break;
      }
    }
    setUpload(null);
    abort.current = null;
    refresh();
  }

  if (!deployed) return <EmptyState title="Files are available once the website is deployed" />;
  const entries = q.data?.entries ?? [];
  const crumbs = dir.split("/").filter(Boolean);
  const allSelected = entries.length > 0 && entries.every((e) => selected.has(e.name));

  const folders = entries.filter((e) => e.type === "dir").length;
  const files = entries.length - folders;
  const iconTool = "inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-line-strong bg-surface text-muted transition hover:bg-surface-2 hover:text-fg disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className="flex flex-col gap-3">
      {!canWrite && <Notice tone="warning">This website is suspended. Files are read-only.</Notice>}
      <TabCard icon={FolderOpen} title="File manager" flush
        description={q.data ? `${folders} folder${folders === 1 ? "" : "s"} · ${files} file${files === 1 ? "" : "s"} in this folder` : "Files run as the website's own user and stay inside its home."}
        action={canWrite && (
          <>
            <button type="button" className={tabSecondaryClass} onClick={() => setCreating("folder")}><FolderPlus size={15} />New folder</button>
            <button type="button" className={tabSecondaryClass} onClick={() => setCreating("file")}><FilePlus size={15} />New file</button>
            <button type="button" className={tabPrimaryClass} disabled={Boolean(upload)} onClick={() => fileInput.current?.click()}><Upload size={15} />Upload</button>
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) void uploadFiles(e.target.files); e.target.value = ""; }} />
          </>
        )}>
        <div className="flex items-center gap-2 border-b border-line bg-surface-2/50 px-[12px] py-[8px]">
          <button type="button" className={iconTool} disabled={dir === "/"} onClick={() => { setDir(parent(dir)); setSelected(new Set()); }} aria-label="Up one folder" title="Up one folder"><ArrowUp size={15} /></button>
          <nav aria-label="Folder path" className="flex min-w-0 flex-1 flex-wrap items-center gap-1 font-mono text-[12.5px] break-all">
            <button type="button" className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-accent hover:bg-accent-soft" onClick={() => setDir("/")}><Home size={13} />home</button>
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight size={13} className="text-subtle" aria-hidden />
                <button type="button" className={i === crumbs.length - 1 ? "rounded-md px-1.5 py-0.5 font-semibold text-fg hover:bg-surface-3" : "rounded-md px-1.5 py-0.5 text-accent hover:bg-accent-soft"} onClick={() => setDir("/" + crumbs.slice(0, i + 1).join("/"))}>{c}</button>
              </span>
            ))}
          </nav>
          <button type="button" className={iconTool} onClick={refresh} aria-label="Refresh" title="Refresh"><RefreshCw size={15} className={q.isFetching ? "animate-spin" : undefined} /></button>
        </div>
        {upload && (
          <div className="flex flex-wrap items-center gap-3 border-b border-line px-[16px] py-[10px] text-[12.5px]">
            <Upload size={15} className="shrink-0 text-accent" />
            <span className="min-w-0 flex-1 truncate">Uploading <span className="font-medium">{upload.name}</span></span>
            <div className="h-[7px] w-40 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={Math.round((upload.sent / Math.max(upload.size, 1)) * 100)} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full bg-accent" style={{ width: `${(upload.sent / Math.max(upload.size, 1)) * 100}%` }} />
            </div>
            <span className="tabular-nums text-muted">{formatBytes(upload.sent)} / {formatBytes(upload.size)}</span>
            <Button size="sm" variant="ghost" onClick={() => abort.current?.abort()}>Cancel</Button>
          </div>
        )}
        {selected.size > 0 && canWrite && (
          <div className="flex items-center gap-3 border-b border-line bg-accent-soft/50 px-[16px] py-[8px] text-[12.5px]">
            <span className="font-medium">{selected.size} selected</span>
            <Button size="sm" variant="danger-ghost" icon={<Trash2 size={13} />} onClick={() => setDeleting([...selected].map((n) => join(dir, n)))}>Delete</Button>
            <button type="button" className="ml-auto text-[12px] text-muted hover:text-fg" onClick={() => setSelected(new Set())}>Clear selection</button>
          </div>
        )}
        {q.error ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} className="m-3" />
        ) : q.isPending ? (
          <p className="flex items-center gap-2 px-[16px] py-6 text-[13px] text-muted"><RefreshCw size={14} className="animate-spin" />Reading files from the server…</p>
        ) : !entries.length ? (
          <EmptyState icon={Folder} title="This folder is empty" description={canWrite ? "Upload files or create a new one." : undefined} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-left text-[9.5px] font-medium tracking-[0.06em] text-muted uppercase">
                <tr className="h-[38px] border-b border-line">
                  {canWrite && <th className="w-10 pr-1 pl-[16px]"><input type="checkbox" aria-label="Select all" className="h-4 w-4 accent-[var(--hd-accent)]" checked={allSelected} onChange={(ev) => setSelected(ev.target.checked ? new Set(entries.map((e) => e.name)) : new Set())} /></th>}
                  <th className={canWrite ? "px-3 font-medium" : "pr-3 pl-[16px] font-medium"}>Name</th>
                  <th className="px-3 text-right font-medium">Size</th>
                  <th className="hidden px-3 font-medium md:table-cell">Modified</th>
                  <th className="hidden px-3 font-medium sm:table-cell">Permissions</th>
                  <th className="w-12 pr-[12px] pl-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const path = join(dir, e.name);
                  const Icon = e.type === "dir" ? Folder : e.type === "symlink" ? Link2 : File;
                  const isSel = selected.has(e.name);
                  return (
                    <tr key={e.name} className={isSel ? "h-[46px] border-b border-line bg-accent-soft/40 last:border-0" : "h-[46px] border-b border-line last:border-0 hover:bg-surface-2/60"}>
                      {canWrite && (
                        <td className="pr-1 pl-[16px]">
                          <input type="checkbox" aria-label={`Select ${e.name}`} className="h-4 w-4 accent-[var(--hd-accent)]" checked={isSel} onChange={(ev) => { const next = new Set(selected); if (ev.target.checked) next.add(e.name); else next.delete(e.name); setSelected(next); }} />
                        </td>
                      )}
                      <td className={canWrite ? "max-w-0 px-3" : "max-w-0 pr-3 pl-[16px]"}>
                        <span className="flex min-w-0 items-center gap-[10px]">
                          <span className={e.type === "dir" ? "flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent" : "flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-lg bg-surface-3 text-muted"} aria-hidden>
                            <Icon size={15} strokeWidth={1.9} />
                          </span>
                          <button
                            type="button"
                            className={e.type === "dir" ? "min-w-0 truncate text-left font-medium hover:text-accent" : "min-w-0 truncate text-left hover:text-accent"}
                            onClick={() => (e.type === "dir" ? (setDir(path), setSelected(new Set())) : e.type === "file" ? setEditing(path) : undefined)}
                            title={e.target ? `${e.name} → ${e.target}` : e.name}
                          >
                            {e.name}
                          </button>
                        </span>
                      </td>
                      <td className="px-3 text-right whitespace-nowrap tabular-nums text-muted">{e.type === "dir" ? "—" : formatBytes(e.size)}</td>
                      <td className="hidden px-3 whitespace-nowrap text-muted md:table-cell">{formatDateTime(e.modified)}</td>
                      <td className="hidden px-3 sm:table-cell"><span className="rounded-md bg-surface-3 px-[7px] py-[2px] font-mono text-[11.5px] text-fg/80">{e.mode.slice(-3)}</span></td>
                      <td className="pr-[12px] pl-3 text-right">
                        <ActionMenu
                          label={`Actions for ${e.name}`}
                          actions={[
                            { label: "Download", icon: <Download size={14} />, hidden: e.type !== "file", onSelect: () => window.open(tools.files.downloadUrl(w.id, path), "_self") },
                            { label: canWrite ? "Edit" : "View", icon: <Pencil size={14} />, hidden: e.type !== "file", onSelect: () => setEditing(path) },
                            { label: "Rename or move", hidden: !canWrite, onSelect: () => setRenaming(e) },
                            { label: "Permissions", hidden: !canWrite || e.type === "symlink", onSelect: () => setChmodding(e) },
                            { label: "Delete", icon: <Trash2 size={14} />, danger: true, hidden: !canWrite, onSelect: () => setDeleting([path]) },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {q.data?.truncated && <p className="border-t border-line px-[16px] py-2 text-[12px] text-muted">Showing the first 5,000 items. Use SFTP for very large folders.</p>}
          </div>
        )}
      </TabCard>
      {creating && <CreateDialog kind={creating} dir={dir} onClose={() => setCreating(null)} onCreate={(name) => tools.files.create(w.id, dir, name, creating)} onDone={refresh} />}
      {renaming && <RenameDialog entry={renaming} dir={dir} onClose={() => setRenaming(null)} onRename={(to) => tools.files.rename(w.id, join(dir, renaming.name), to)} onDone={refresh} />}
      {chmodding && <ChmodDialog entry={chmodding} onClose={() => setChmodding(null)} onSave={(mode) => tools.files.chmod(w.id, join(dir, chmodding.name), mode)} onDone={refresh} />}
      {editing && <EditorDialog path={editing} readOnly={!canWrite} load={() => tools.files.read(w.id, editing)} save={(c) => tools.files.write(w.id, editing, c)} downloadUrl={tools.files.downloadUrl(w.id, editing)} onClose={() => setEditing(null)} onSaved={refresh} />}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={deleting?.length === 1 ? `Delete ${deleting[0]!.split("/").pop()}?` : `Delete ${deleting?.length} items?`}
        description="Folders are deleted with everything inside them. This cannot be undone."
        confirmLabel="Delete"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting)}
      />
    </div>
  );
}

function CreateDialog({ kind, dir, onClose, onCreate, onDone }: { kind: "file" | "folder"; dir: string; onClose(): void; onCreate(name: string): Promise<unknown>; onDone(): void }) {
  const [name, setName] = useState("");
  const m = useMutation({ meta: { inlineError: true }, mutationFn: () => onCreate(name.trim()), onSuccess: () => { toast.success(`${kind === "file" ? "File" : "Folder"} created`); onDone(); onClose(); } });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={kind === "file" ? "New file" : "New folder"} description={`In ${dir}`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!name.trim() || name.includes("/")} onClick={() => m.mutate()}>Create</Button></>}>
      <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder={kind === "file" ? "index.php" : "assets"} onKeyDown={(e) => e.key === "Enter" && name.trim() && m.mutate()} />}</Field>
      {m.error ? <div className="mt-3"><FormError error={m.error} /></div> : null}
    </Dialog>
  );
}

function RenameDialog({ entry, dir, onClose, onRename, onDone }: { entry: FileEntry; dir: string; onClose(): void; onRename(to: string): Promise<unknown>; onDone(): void }) {
  const [to, setTo] = useState(join(dir, entry.name));
  const m = useMutation({ mutationFn: () => onRename(to.trim()), onSuccess: () => { toast.success("Moved"); onDone(); onClose(); } });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Rename or move ${entry.name}`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!to.trim()} onClick={() => m.mutate()}>Save</Button></>}>
      <Field label="New path" hint="Paths start at the website home, e.g. /public/old/index.php">{(p) => <Input {...p} value={to} onChange={(e) => setTo(e.target.value)} className="font-mono" autoFocus />}</Field>
      {m.error ? <div className="mt-3"><FormError error={m.error} /></div> : null}
    </Dialog>
  );
}

function ChmodDialog({ entry, onClose, onSave, onDone }: { entry: FileEntry; onClose(): void; onSave(mode: string): Promise<unknown>; onDone(): void }) {
  const [mode, setMode] = useState(entry.mode.slice(-3));
  const valid = /^[0-7]{3}$/.test(mode);
  const m = useMutation({ mutationFn: () => onSave(mode), onSuccess: () => { toast.success("Permissions updated"); onDone(); onClose(); } });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Permissions of ${entry.name}`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!valid} onClick={() => m.mutate()}>Save</Button></>}>
      <Field label="Mode" hint="Three octal digits for owner, group and others. Files: 640 or 644; folders: 750 or 755.">
        {(p) => <Input {...p} value={mode} onChange={(e) => setMode(e.target.value.trim())} className="w-24 font-mono" maxLength={3} autoFocus />}
      </Field>
      {m.error ? <div className="mt-3"><FormError error={m.error} /></div> : null}
    </Dialog>
  );
}

function EditorDialog({ path, readOnly, load, save, downloadUrl, onClose, onSaved }: {
  path: string; readOnly: boolean; load(): Promise<{ content: string; size: number }>; save(content: string): Promise<unknown>; downloadUrl: string; onClose(): void; onSaved(): void;
}) {
  const q = useQuery({ queryKey: ["file-content", path], queryFn: load, retry: false, gcTime: 0 });
  const [draft, setDraft] = useState<string | null>(null);
  const content = draft ?? q.data?.content ?? "";
  const dirty = draft !== null && draft !== q.data?.content;
  const m = useMutation({ mutationFn: () => save(content), onSuccess: () => { toast.success("Saved"); setDraft(null); onSaved(); void q.refetch(); } });
  return (
    <Dialog open onOpenChange={(o) => !o && (!dirty || window.confirm("Discard unsaved changes?")) && onClose()} title={path.split("/").pop()} description={<span className="font-mono">{path}</span>} size="lg"
      footer={
        <>
          <a href={downloadUrl} className="mr-auto text-[13px] text-accent hover:underline">Download</a>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {!readOnly && <Button variant="primary" loading={m.isPending} disabled={!dirty || !q.data} onClick={() => m.mutate()}>Save</Button>}
        </>
      }>
      {q.error ? <FormError error={q.error} />
        : q.isPending ? <p className="text-[13px] text-muted">Loading…</p>
        : <Textarea value={content} readOnly={readOnly} onChange={(e) => setDraft(e.target.value)} spellCheck={false} aria-label="File content"
            className="h-[60vh] w-full resize-none font-mono text-[12.5px] leading-relaxed"
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); if (dirty && !readOnly) m.mutate(); } }} />}
      {m.error ? <div className="mt-3"><FormError error={m.error} /></div> : null}
    </Dialog>
  );
}
